<script lang="ts">
	import { format } from 'svelte-i18n';
	import { tick } from 'svelte';
	import type { CardDiscoveryGroup, CardDiscoveryRole } from '../core/card-engine/types';
	import type { NodeTypeConfig } from '../core/config/node-types';
	import type {
		FlowCatalogBrowseMode,
		FlowCatalogCard,
		FlowCatalogDevice,
		FlowCatalogRole,
	} from '../core/catalog/flow-card-catalog';
	import { devices, devices_settings, devices_status, rooms, user_data } from '../../../stores';
	import CatalogCardResult from './catalog/CatalogCardResult.svelte';
	import CatalogNavigationItem from './catalog/CatalogNavigationItem.svelte';
	import { Button, Input, Modal, Tab, Tabs, Text } from '@Shelly/shelly-design-system';
	import { _init_action_components_v2 } from '../../Scenes/SceneWizard/Process/Do/Device/helpers';
	import { _init_conditions_components } from '../../Scenes/SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/common-helpers';
	import {
		buildDeviceActionCards,
		buildDeviceConditionCards,
		buildFlowCatalogModel,
		nodeBelongsToCatalogRole,
		searchFlowCatalog,
	} from '../core/catalog/flow-card-catalog';
	import { resolveVirtualComponentDescriptors } from '../core/card-engine/virtual-components';
	import { getNodeAccountAvailability } from '../core/config/node-account-availability';
	import { getNodeTypesByCategory } from '../core/config/node-types';
	import { summarizePrivateSearchQuery, trackFlowUxEvent } from '../core/ux/flow-ux-telemetry';
	import { isSelectableForFlowDeviceAction } from '../helpers/doActionDeviceEligibility';
	import { isSelectableForFlowDeviceCondition } from '../helpers/whenConditionDeviceEligibility';
	import { real_id } from '../../../helpers/interface_helpers';

	export let showModal = false;
	export let isMobile = false;
	export let onClose: () => void;
	export let onAdd: (
		nodeType: string,
		paletteKey: string,
		initialData?: Record<string, unknown>,
	) => void;
	export let entryPoint: 'toolbar' | 'canvas' | 'connection' = 'toolbar';
	export let allowedRoles: CardDiscoveryRole[] | undefined;
	export let initialView: BrowserView | undefined;

	type BrowserView = 'when' | 'if' | 'then' | 'all';
	type MobilePane = 'browse' | 'rooms' | 'devices' | 'criteria' | 'cards';

	const roleItems: Array<{ id: FlowCatalogRole; label: string; icon: string }> = [
		{ id: 'when', label: 'When', icon: 'fas fa-bolt' },
		{ id: 'and', label: 'And', icon: 'fas fa-circle-question' },
		{ id: 'do', label: 'Do', icon: 'fas fa-play' },
	];
	const browseItems: Array<{
		id: FlowCatalogBrowseMode;
		label: string;
		description: string;
		icon: string;
	}> = [
		{ id: 'all', label: 'All cards', description: 'See every compatible card', icon: 'fa-grid-2' },
		{ id: 'rooms', label: 'Rooms', description: 'Choose a room and device', icon: 'fa-house' },
		{
			id: 'criteria',
			label: 'Criteria',
			description: 'Browse by purpose or information',
			icon: 'fa-layer-group',
		},
	];
	const criterionIcons: Record<CardDiscoveryGroup, string> = {
		devices: 'fa-microchip',
		time: 'fa-clock',
		weather: 'fa-cloud-sun',
		energy: 'fa-bolt',
		presence: 'fa-person-rays',
		scenes: 'fa-clapperboard-play',
		notifications: 'fa-bell',
		variables: 'fa-code',
		scripts: 'fa-file-code',
		'flow-logic': 'fa-code-branch',
		system: 'fa-gear',
	};

	let activeRole: FlowCatalogRole = 'when';
	let browseMode: FlowCatalogBrowseMode = 'all';
	let selectedRoomId = '';
	let selectedDeviceId = '';
	let selectedCriterion: CardDiscoveryGroup | '' = '';
	let searchQuery = '';
	let mobilePane: MobilePane = 'browse';
	let catalogOpenedAt = 0;
	let wasOpen = false;
	let selectionMade = false;
	let lastSearchSignature = '';
	let searchInputRef: HTMLElement | undefined;
	let viewportWidth = 0;

	$: isCompactBrowser = isMobile || (viewportWidth > 0 && viewportWidth <= 900);

	$: catalog = getNodeTypesByCategory();
	$: allNodes = Object.values(catalog).flat() as NodeTypeConfig[];
	$: baseNodes = allNodes.filter(
		(node) => !allowedRoles?.length || allowedRoles.includes(node.discovery.role),
	);
	$: availableRoleItems = roleItems.filter((role) =>
		baseNodes.some((node) => nodeBelongsToCatalogRole(node, role.id)),
	);
	$: isPremium = Boolean($user_data?.premium);
	$: virtualComponentCount = resolveVirtualComponentDescriptors({
		devices: $devices,
		settings: $devices_settings,
		status: $devices_status,
	}).length;
	$: roomItems = Object.entries($rooms ?? {})
		.map(([id, room]: [string, any]) => ({
			id: String(id),
			name: String(room?.name || $format('label_unnamed_room', { default: 'Unnamed room' })),
		}))
		.sort((a, b) => a.name.localeCompare(b.name));
	$: deviceItems = Object.values($devices ?? {})
		.filter((device: any) => device && !device.deleted)
		.map((device: any): FlowCatalogDevice => buildCatalogDevice(device))
		.sort((a, b) => a.roomName.localeCompare(b.roomName) || a.name.localeCompare(b.name));
	$: model = buildFlowCatalogModel({
		nodes: baseNodes,
		role: activeRole,
		devices: deviceItems,
		roomItems,
		availabilityFor: nodeAvailability,
	});
	$: selectedRoom = model.rooms.find((room) => room.id === selectedRoomId);
	$: selectedDevice = deviceItems.find((device) => device.id === selectedDeviceId);
	$: selectedDeviceCards = selectedDevice ? cardsForDevice(selectedDevice) : [];
	$: criterionCards = selectedCriterion
		? model.cards.filter((card) => card.group === selectedCriterion)
		: [];
	$: allDeviceCards = searchQuery.trim()
		? deviceItems.flatMap((device) => cardsForDevice(device))
		: [];
	$: searchResults = searchQuery.trim()
		? searchFlowCatalog([...model.cards, ...allDeviceCards], searchQuery)
		: [];
	$: visibleCards = searchQuery.trim()
		? searchResults
		: browseMode === 'criteria'
			? criterionCards
			: browseMode === 'rooms'
				? selectedDeviceCards
				: model.cards;
	$: cardGroups = groupCards(visibleCards);
	$: resultCount = visibleCards.length;
	$: breadcrumbs = mobileBreadcrumbs();
	$: if (showModal && !wasOpen) openCatalog();
	$: if (!showModal && wasOpen) wasOpen = false;
	$: if (showModal) trackSearchChange();

	function eventValue(event: any): string {
		const detail = event?.detail;
		if (typeof detail === 'string') return detail;
		if (detail && typeof detail === 'object' && 'value' in detail) return String(detail.value ?? '');
		return String(event?.target?.value ?? '');
	}

	function preferredRole(view?: BrowserView, roles?: CardDiscoveryRole[]): FlowCatalogRole {
		if (view === 'if') return 'and';
		if (view === 'then') return 'do';
		if (view === 'when') return 'when';
		if (roles?.some((role) => role === 'trigger')) return 'when';
		if (roles?.some((role) => role === 'condition' || role === 'branch')) return 'and';
		if (roles?.some((role) => role === 'action' || role === 'state')) return 'do';
		return 'when';
	}

	async function openCatalog() {
		wasOpen = true;
		selectionMade = false;
		catalogOpenedAt = Date.now();
		const requestedRole = preferredRole(initialView, allowedRoles);
		activeRole = availableRoleItems.some((role) => role.id === requestedRole)
			? requestedRole
			: (availableRoleItems[0]?.id ?? 'when');
		browseMode = 'all';
		mobilePane = 'browse';
		trackFlowUxEvent({ name: 'catalog_opened', surface: 'catalog', entryPoint });
		await tick();
		searchInputRef?.focus();
	}

	function buildCatalogDevice(device: any): FlowCatalogDevice {
		const statusKey = real_id(device.id);
		const status = ($devices_status as any)?.[statusKey];
		const settings = ($devices_settings as any)?.[statusKey];
		let actionComponents: any[] = [];
		let conditionComponents: any[] = [];
		try {
			actionComponents = _init_action_components_v2(device, status, settings) ?? [];
		} catch {
			actionComponents = [];
		}
		try {
			conditionComponents = _init_conditions_components(device, status, settings) ?? [];
		} catch {
			conditionComponents = [];
		}
		const room = ($rooms as any)?.[device.room_id];
		return {
			id: String(device.id),
			name: String(
				device.name ||
					device.type ||
					$format('label_unnamed_device', { default: 'Unnamed device' }),
			),
			roomId: String(device.room_id ?? ''),
			roomName: String(room?.name || $format('label_other_devices', { default: 'Other devices' })),
			channel: String(device.channel ?? 0),
			icon: String(device.icon || 'fa-microchip'),
			device,
			actionComponents,
			conditionComponents,
		};
	}

	function nodeAvailability(
		node: NodeTypeConfig,
		device?: FlowCatalogDevice,
	): { available: boolean; label?: string } {
		return getNodeAccountAvailability(node, {
			devices: device ? [device.device] : $devices,
			virtualComponentCount,
		});
	}

	function cardsForDevice(device: FlowCatalogDevice): FlowCatalogCard[] {
		const statusKey = real_id(device.id);
		const status = ($devices_status as any)?.[statusKey];
		const settings = ($devices_settings as any)?.[statusKey];
		if (activeRole === 'do') {
			const actionNode = baseNodes.find((node) => (node.paletteKey ?? node.type) === 'action');
			if (!actionNode || !isSelectableForFlowDeviceAction(device.device, status, settings))
				return [];
			return buildDeviceActionCards(device, actionNode, nodeAvailability(actionNode, device));
		}
		const paletteKey = activeRole === 'when' ? 'condition' : 'deviceCheck';
		const conditionNode = baseNodes.find((node) => (node.paletteKey ?? node.type) === paletteKey);
		if (!conditionNode || !isSelectableForFlowDeviceCondition(device.device, status, settings))
			return [];
		return buildDeviceConditionCards(
			device,
			conditionNode,
			activeRole,
			nodeAvailability(conditionNode, device),
		);
	}

	function groupCards(cards: FlowCatalogCard[]) {
		const groups = new Map<string, { id: string; label: string; cards: FlowCatalogCard[] }>();
		for (const card of cards) {
			const id = searchQuery.trim()
				? 'search'
				: browseMode === 'rooms'
					? card.identity
					: card.group;
			const label = searchQuery.trim()
				? $format('label_search_results', { default: 'Search results' })
				: browseMode === 'rooms'
					? card.identity
					: card.groupLabel;
			const group = groups.get(id) ?? { id, label, cards: [] };
			group.cards.push(card);
			groups.set(id, group);
		}
		return [...groups.values()];
	}

	function setRole(role: FlowCatalogRole) {
		activeRole = role;
		selectedDeviceId = '';
		selectedCriterion = '';
		if (browseMode === 'rooms' && selectedRoomId) mobilePane = 'devices';
		else if (browseMode === 'criteria') mobilePane = 'criteria';
		trackNavigation();
	}

	function selectBrowseMode(mode: FlowCatalogBrowseMode) {
		browseMode = mode;
		selectedRoomId = '';
		selectedDeviceId = '';
		selectedCriterion = '';
		mobilePane = mode === 'all' ? 'cards' : mode;
		trackNavigation();
	}

	function selectRoom(roomId: string) {
		selectedRoomId = roomId;
		selectedDeviceId = '';
		mobilePane = 'devices';
		trackNavigation();
	}

	function selectDevice(deviceId: string) {
		selectedDeviceId = deviceId;
		mobilePane = 'cards';
		trackNavigation();
	}

	function selectCriterion(criterion: CardDiscoveryGroup) {
		selectedCriterion = criterion;
		mobilePane = 'cards';
		trackNavigation();
	}

	function trackNavigation() {
		trackFlowUxEvent({
			name: 'catalog_view_changed',
			surface: 'catalog',
			view: activeRole,
			browsePath: browseMode,
			depth: navigationDepth(),
		});
	}

	function navigationDepth(): number {
		if (browseMode === 'all') return 1;
		if (browseMode === 'criteria') return selectedCriterion ? 2 : 1;
		return selectedDeviceId ? 3 : selectedRoomId ? 2 : 1;
	}

	function trackSearchChange() {
		const signature = `${activeRole}|${searchQuery}|${resultCount}`;
		if (signature === lastSearchSignature) return;
		lastSearchSignature = signature;
		if (!searchQuery.trim()) return;
		trackFlowUxEvent({
			name: 'catalog_search_changed',
			surface: 'catalog',
			...summarizePrivateSearchQuery(searchQuery),
			resultCount,
			role: activeRole,
		});
	}

	function addCard(card: FlowCatalogCard) {
		if (card.premiumOnly && !isPremium) return;
		if (!card.availability.available) return;
		selectionMade = true;
		trackFlowUxEvent({
			name: 'catalog_selection',
			surface: 'catalog',
			paletteKey: card.paletteKey,
			view: activeRole,
			browsePath: browseMode,
			depth: navigationDepth(),
			resultCount,
			hadSearch: Boolean(searchQuery.trim()),
			elapsedMs: Math.max(0, Date.now() - catalogOpenedAt),
		});
		onAdd(card.nodeType, card.paletteKey, card.initialData);
		reset();
	}

	function goBackMobile() {
		if (searchQuery.trim()) {
			searchQuery = '';
			return;
		}
		if (mobilePane === 'cards') {
			if (browseMode === 'rooms') mobilePane = 'devices';
			else if (browseMode === 'criteria') mobilePane = 'criteria';
			else mobilePane = 'browse';
			return;
		}
		if (mobilePane === 'devices') mobilePane = 'rooms';
		else mobilePane = 'browse';
	}

	function mobileBreadcrumbs(): string[] {
		const role = roleItems.find((item) => item.id === activeRole)?.label ?? '';
		const mode = browseItems.find((item) => item.id === browseMode)?.label ?? '';
		const result = [role];
		if (mobilePane !== 'browse') result.push(mode);
		if (selectedRoom) result.push(selectedRoom.name);
		if (selectedDevice) result.push(selectedDevice.name);
		if (selectedCriterion) {
			result.push(model.criteria.find((item) => item.id === selectedCriterion)?.label ?? '');
		}
		return result.filter(Boolean);
	}

	function reset() {
		searchQuery = '';
		browseMode = 'all';
		selectedRoomId = '';
		selectedDeviceId = '';
		selectedCriterion = '';
		mobilePane = 'browse';
		activeRole = 'when';
		lastSearchSignature = '';
	}

	function close() {
		if (!selectionMade) {
			trackFlowUxEvent({
				name: 'catalog_abandoned',
				surface: 'catalog',
				view: activeRole,
				browsePath: browseMode,
				depth: navigationDepth(),
				hadSearch: Boolean(searchQuery.trim()),
				elapsedMs: Math.max(0, Date.now() - catalogOpenedAt),
			});
		}
		reset();
		onClose();
	}
</script>

<svelte:window
	bind:innerWidth={viewportWidth}
	on:keydown={(event) => {
		if (showModal && isCompactBrowser && event.key === 'Escape' && mobilePane !== 'browse') {
			event.preventDefault();
			goBackMobile();
		}
	}}
/>

<Modal
	class="flow-card-catalog-modal"
	{showModal}
	title={$format('title_add_to_flow', { default: 'Add to Flow' })}
	type={isCompactBrowser ? 'fullScreen' : 'default'}
	backdropClose={false}
	on:close={close}
>
	<svelte:fragment slot="toolbar">
		<div class="catalog-toolbar">
			{#if isCompactBrowser && (mobilePane !== 'browse' || searchQuery.trim())}
				<Button
					variant="secondary"
					icon="fas fa-arrow-left"
					aria-label={$format('button_back_in_card_browser', {
						default: 'Back in card browser',
					})}
					on:click={goBackMobile}
				/>
			{/if}
			<div class="catalog-search">
				<Input
					bind:ref={searchInputRef}
					id="flow-card-search"
					label={$format('label_search_flow_cards', {
						default: 'Search cards, rooms, or devices',
					})}
					aria-label={$format('label_search_flow_cards', {
						default: 'Search cards, rooms, or devices',
					})}
					icon="fas fa-search"
					clearButton
					width="full"
					value={searchQuery}
					on:input={(event) => (searchQuery = eventValue(event))}
				/>
			</div>
		</div>
	</svelte:fragment>

	<svelte:fragment slot="content">
		<div class="catalog-shell">
			<Tabs useNewLayout class="catalog-role-tabs">
				{#each availableRoleItems as role}
					<Tab
						useNewLayout
						icon={role.icon}
						active={activeRole === role.id}
						on:click={() => setRole(role.id)}
					>
						{$format(`label_flow_role_${role.id}`, { default: role.label })}
					</Tab>
				{/each}
			</Tabs>

			{#if isCompactBrowser && breadcrumbs.length > 1}
				<Text styleType="tiny" class="catalog-breadcrumbs text-color-3">
					{breadcrumbs.join(' / ')}
				</Text>
			{/if}

			<div class:searching={Boolean(searchQuery.trim())} class="catalog-columns">
				<section
					class:mobile-hidden={isCompactBrowser && mobilePane !== 'browse'}
					class="catalog-column browse-column"
				>
					<header>
						<Text styleType="label-large"
							>{$format('label_browse', { default: 'Browse' })}</Text
						>
					</header>
					<div class="column-list">
						{#each browseItems as item}
							<CatalogNavigationItem
								title={$format(`label_flow_browse_${item.id}`, { default: item.label })}
								subtitle={$format(`description_flow_browse_${item.id}`, {
									default: item.description,
								})}
								icon={`fas ${item.icon}`}
								selected={browseMode === item.id}
								count={item.id === 'all' ? model.cards.length : undefined}
								ariaLabel={$format('label_browse_flow_cards_by', {
									default: 'Browse Flow cards by {mode}',
									values: { mode: item.label },
								})}
								on:select={() => selectBrowseMode(item.id)}
							/>
						{/each}
					</div>
				</section>

				{#if !searchQuery.trim() && browseMode === 'rooms'}
					<section
						class:mobile-hidden={isCompactBrowser && mobilePane !== 'rooms'}
						class="catalog-column"
					>
						<header>
							<Text styleType="label-large"
								>{$format('label_rooms', { default: 'Rooms' })}</Text
							>
						</header>
						<div class="column-list">
							{#each model.rooms as room}
								<CatalogNavigationItem
									title={room.name}
									subtitle={$format('label_device_count', {
										default: '{count} devices',
										values: { count: room.devices.length },
									})}
									icon="fas fa-door-open"
									selected={selectedRoomId === room.id}
									count={room.devices.length}
									ariaLabel={$format('label_select_room_for_flow_card', {
										default: 'Select room {room}',
										values: { room: room.name },
									})}
									on:select={() => selectRoom(room.id)}
								/>
							{/each}
						</div>
					</section>

					<section
						class:mobile-hidden={isCompactBrowser && mobilePane !== 'devices'}
						class="catalog-column"
					>
						<header>
							<Text styleType="label-large"
								>{$format('label_devices', { default: 'Devices' })}</Text
							>
						</header>
						<div class="column-list">
							{#if selectedRoom}
								{#each selectedRoom.devices as device}
									{@const count = cardsForDevice(device).length}
									<CatalogNavigationItem
										title={device.name}
										subtitle={device.roomName}
										icon={`fas ${device.icon}`}
										selected={selectedDeviceId === device.id}
										{count}
										ariaLabel={$format('label_select_device_for_flow_card', {
											default: 'Select device {device}',
											values: { device: device.name },
										})}
										on:select={() => selectDevice(device.id)}
									/>
								{/each}
							{:else}
								<div class="column-empty">
									<Text
										>{$format('description_select_room_first', {
											default: 'Select a room to see its devices.',
										})}</Text
									>
								</div>
							{/if}
						</div>
					</section>
				{/if}

				{#if !searchQuery.trim() && browseMode === 'criteria'}
					<section
						class:mobile-hidden={isCompactBrowser && mobilePane !== 'criteria'}
						class="catalog-column criteria-column"
					>
						<header>
							<Text styleType="label-large"
								>{$format('label_criteria', { default: 'Criteria' })}</Text
							>
						</header>
						<div class="column-list">
							{#each model.criteria as criterion}
								<CatalogNavigationItem
									title={$format(
										`label_flow_criteria_${criterion.id.replace('-', '_')}`,
										{
											default: criterion.label,
										},
									)}
									icon={`fas ${criterionIcons[criterion.id]}`}
									selected={selectedCriterion === criterion.id}
									count={criterion.count}
									ariaLabel={$format('label_select_flow_criterion', {
										default: 'Select criterion {criterion}',
										values: { criterion: criterion.label },
									})}
									on:select={() => selectCriterion(criterion.id)}
								/>
							{/each}
						</div>
					</section>
				{/if}

				<section
					class:mobile-hidden={isCompactBrowser &&
						mobilePane !== 'cards' &&
						!searchQuery.trim()}
					class:all-results={browseMode === 'all' && !searchQuery.trim()}
					class:wide-results={browseMode === 'criteria' || Boolean(searchQuery.trim())}
					class="catalog-column cards-column"
					aria-live="polite"
				>
					<header>
						<Text styleType="label-large">
							{searchQuery.trim()
								? $format('label_search_results', { default: 'Search results' })
								: $format('label_cards', { default: 'Cards' })}
						</Text>
						<Text styleType="tiny" class="text-color-3">
							{$format('label_card_match_count', {
								default: '{count} matches',
								values: { count: resultCount },
							})}
						</Text>
					</header>
					<div class="cards-scroll">
						{#if visibleCards.length}
							{#each cardGroups as group (group.id)}
								<div class="card-group">
									{#if cardGroups.length > 1}
										<Text
											styleType="label-small"
											class="card-group-title text-color-3"
										>
											{group.label}
										</Text>
									{/if}
									<div class="result-grid">
										{#each group.cards as card (card.id)}
											<CatalogCardResult
												{card}
												{isPremium}
												on:select={(event) => addCard(event.detail)}
											/>
										{/each}
									</div>
								</div>
							{/each}
						{:else}
							<div class="column-empty" role="status">
								<Text styleType="label-large">
									{$format('title_no_matching_flow_cards', {
										default: 'No matching cards',
									})}
								</Text>
								<Text class="text-color-3">
									{$format('description_no_matching_flow_cards', {
										default: 'Try another room, criterion, or search phrase.',
									})}
								</Text>
							</div>
						{/if}
					</div>
				</section>
			</div>
		</div>
	</svelte:fragment>

	<div slot="footer" class="modal-footer">
		<Button
			variant="secondary"
			aria-label={$format('button_cancel_adding_flow_card', {
				default: 'Cancel adding a Flow card',
			})}
			on:click={close}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
	</div>
</Modal>

<style>
	.catalog-toolbar {
		display: flex;
		align-items: center;
		gap: var(--spacing-s, 8px);
		width: min(1120px, calc(100vw - 48px));
	}
	.catalog-search {
		min-width: 0;
		flex: 1;
	}
	.catalog-shell {
		display: flex;
		width: min(1120px, calc(100vw - 48px));
		height: min(680px, calc(100vh - 220px));
		min-height: 420px;
		flex-direction: column;
		overflow: hidden;
		border: 1px solid var(--color-3, rgba(148, 163, 184, 0.2));
		border-radius: var(--radius-large, 12px);
		background: var(--color-0, #171819);
	}
	:global(.catalog-role-tabs) {
		flex: 0 0 auto;
		padding: var(--spacing-xs, 4px) var(--spacing-s, 8px) 0;
	}
	:global(.catalog-breadcrumbs) {
		padding: var(--spacing-xs, 4px) var(--spacing-m, 12px);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	.catalog-columns {
		display: grid;
		min-height: 0;
		flex: 1;
		grid-template-columns: 230px 230px 230px minmax(320px, 1fr);
		overflow: hidden;
		border-top: 1px solid var(--color-3, rgba(148, 163, 184, 0.18));
	}
	.catalog-columns.searching {
		grid-template-columns: 230px minmax(0, 1fr);
	}
	.catalog-column {
		display: flex;
		min-width: 0;
		min-height: 0;
		flex-direction: column;
		border-right: 1px solid var(--color-3, rgba(148, 163, 184, 0.18));
	}
	.catalog-column:last-child {
		border-right: 0;
	}
	.catalog-column > header {
		display: flex;
		min-height: 44px;
		align-items: center;
		justify-content: space-between;
		gap: var(--spacing-s, 8px);
		padding: var(--spacing-s, 8px) var(--spacing-m, 12px);
		border-bottom: 1px solid var(--color-3, rgba(148, 163, 184, 0.14));
	}
	.column-list,
	.cards-scroll {
		display: flex;
		min-height: 0;
		flex: 1;
		flex-direction: column;
		gap: var(--spacing-s, 8px);
		overflow-y: auto;
		padding: var(--spacing-s, 8px);
		overscroll-behavior: contain;
	}
	.cards-column.wide-results {
		grid-column: span 2;
	}
	.cards-column.all-results {
		grid-column: span 3;
	}
	.catalog-columns.searching .cards-column {
		grid-column: auto;
	}
	.card-group {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs, 4px);
	}
	:global(.card-group-title) {
		padding: var(--spacing-xs, 4px) var(--spacing-xs, 4px) 0;
	}
	.result-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
		gap: var(--spacing-s, 8px);
	}
	.column-empty {
		display: flex;
		min-height: 160px;
		align-items: center;
		justify-content: center;
		flex-direction: column;
		gap: var(--spacing-xs, 4px);
		padding: var(--spacing-l, 24px);
		text-align: center;
	}
	.modal-footer {
		display: flex;
		width: 100%;
		justify-content: flex-end;
	}
	:global(.modal-container.flow-card-catalog-modal.type-default) {
		width: min(1120px, calc(100vw - 24px));
		max-width: calc(100vw - 24px);
	}

	@media (max-width: 900px) {
		.catalog-toolbar,
		.catalog-shell {
			width: 100%;
		}
		.catalog-shell {
			height: 100%;
			min-height: 0;
			border: 0;
			border-radius: 0;
		}
		.catalog-columns,
		.catalog-columns.searching {
			display: block;
			overflow: hidden;
		}
		.catalog-column {
			height: 100%;
			border-right: 0;
		}
		.catalog-column.mobile-hidden {
			display: none;
		}
		.result-grid {
			grid-template-columns: 1fr;
		}
	}
</style>
